/*
 * air-sleeve.js — native behaviour for the converted pulsio-eu Compression
 * Sleeve product page (sections air-sleeve-s1..s4). Replaces the Replo runtime
 * for the interactions the page uses:
 *   1. Product image carousel (s1) — transform based, prev/next + dots + drag.
 *   2. Collapsibles / accordions (Radix-style [data-replo-collapsible]).
 *   3. Sleek-style scroll reveals + resume Replo "alchemy" fade animations
 *      (otherwise elements stay at opacity:0 once the runtime is gone).
 *   4. Product-card click-through (data-replo-product-handle).
 * The native <form action="/cart/add"> add-to-cart needs no JS.
 *
 * SAFETY: if the Replo runtime is still present on the page (baseline / not yet
 * stripped) this script no-ops so the two never fight over the DOM.
 * Dependency-free, idempotent per element.
 */
(function () {
  if (window.__airSleeveInit) return;
  window.__airSleeveInit = true;

  // Bail while the Replo runtime owns the DOM (parity baseline).
  if (
    document.querySelector('script[src*="replocdn.com"]') ||
    document.querySelector('[data-runtime-version]')
  ) {
    return;
  }

  var TRANSITION = 'transform 450ms cubic-bezier(0.22, 0.61, 0.36, 1)';
  var REVEAL_OPTS = { rootMargin: '0px 0px -8% 0px', threshold: 0.12 };
  var STAGGER_MS = 80;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }
  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }
  function scope() {
    return document.querySelectorAll('[id^="replo-section-"]');
  }

  /* ---------------- Carousel ---------------- */
  function currentX(track) {
    var m = /translate3d\(\s*(-?[\d.]+)px/.exec(track.style.transform || '');
    return m ? parseFloat(m[1]) : 0;
  }
  function initCarousel(root) {
    if (window.AirCarousel && window.AirCarousel.init) {
      return window.AirCarousel.init(root);
    }
  }

  /* ---------------- Collapsibles (accordions) ---------------- */
  function measure(el) {
    // natural pixel height with height:auto, robust against nested collapsed
    // heights. Restores prior inline height/transition afterwards.
    var prevH = el.style.height, prevT = el.style.transition, prevO = el.style.overflow;
    el.style.transition = 'none';
    el.style.height = 'auto';
    el.style.overflow = 'visible';
    var h = el.getBoundingClientRect().height || el.scrollHeight;
    el.style.height = prevH;
    el.style.transition = prevT;
    el.style.overflow = prevO;
    return h;
  }
  function initCollapsible(root) {
    if (root.__airCollapsible) return;
    root.__airCollapsible = true;
    var kids = Array.prototype.filter.call(root.children, function (n) {
      return n.nodeType === 1;
    });
    if (kids.length < 2) return;
    var trigger = root.querySelector('[role="button"]') || kids[0];
    var content = null;
    for (var i = 0; i < kids.length; i++) {
      if (kids[i] === trigger || kids[i].contains(trigger)) { content = kids[i + 1]; break; }
    }
    content = content || kids[kids.length - 1];
    if (!content || content === trigger) return;

    var open = root.getAttribute('data-state') === 'open';
    content.style.overflow = 'hidden';
    content.style.willChange = 'height';

    function show() {
      // Undo the Radix closed state (hidden attribute / display:none) so the
      // content participates in layout and can be measured/animated.
      content.hidden = false;
      content.removeAttribute('hidden');
      content.style.display = '';
    }
    function applyClosed() {
      content.style.height = '0px';
      content.style.opacity = '0';
      content.style.display = 'none';
    }
    if (!open) applyClosed();
    else { show(); content.style.height = 'auto'; content.style.opacity = '1'; }
    root.setAttribute('data-state', open ? 'open' : 'closed');

    trigger.style.cursor = 'pointer';
    trigger.setAttribute('role', 'button');
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    trigger.addEventListener('click', function (e) {
      if (e.target.closest('a[href]')) return;
      open = !open;
      root.setAttribute('data-state', open ? 'open' : 'closed');
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        show();
        var target = measure(content);
        content.style.transition = 'none';
        content.style.height = '0px';
        content.style.opacity = '0';
        void content.offsetHeight;
        content.style.transition = 'height 260ms cubic-bezier(0.4,0,0.2,1), opacity 260ms';
        content.style.height = target + 'px';
        content.style.opacity = '1';
        window.setTimeout(function () {
          if (root.getAttribute('data-state') === 'open') content.style.height = 'auto';
        }, 280);
      } else {
        content.style.transition = 'none';
        content.style.height = content.getBoundingClientRect().height + 'px';
        void content.offsetHeight;
        content.style.transition = 'height 260ms cubic-bezier(0.4,0,0.2,1), opacity 260ms';
        content.style.height = '0px';
        content.style.opacity = '0';
        window.setTimeout(function () {
          if (root.getAttribute('data-state') === 'closed') content.style.display = 'none';
        }, 280);
      }
    });
  }

  /* ---------------- Reveals + alchemy resume ---------------- */
  function isInView(el) {
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return r.top < vh * 0.92 && r.bottom > 0;
  }
  function hasBg(el) {
    if ((el.style.backgroundImage || '').indexOf('url') !== -1) return true;
    var cs = getComputedStyle(el);
    return cs.backgroundImage && cs.backgroundImage !== 'none';
  }
  function isAlchemyPaused(el) {
    var cs = getComputedStyle(el);
    return cs.animationName && cs.animationName.indexOf('alchemy') !== -1 && cs.animationPlayState === 'paused';
  }
  function revealEl(el) {
    if (el.__airRevealed) return;
    el.__airRevealed = true;
    el.classList.remove('air-reveal-pending');
    el.classList.add('air-reveal-visible');
  }
  function tagReveal(el, variant, delayMs) {
    if (el.__airRevealTagged) return null;
    el.__airRevealTagged = true;
    if (delayMs) el.style.setProperty('--air-reveal-delay', delayMs + 'ms');
    el.classList.add('air-reveal-pending', 'air-reveal--' + (variant || 'fade-up'));
    if (reducedMotion || isInView(el)) { revealEl(el); return null; }
    return el;
  }
  function initMotion() {
    var toObserve = [];
    var alchemyBg = [];
    function observe(el) { if (el && !el.__airRevealed) toObserve.push(el); }

    each(scope(), function (section) {
      each(section.querySelectorAll('[data-replo-carousel]'), function (c) {
        if (isAlchemyPaused(c) && hasBg(c)) { alchemyBg.push(c); return; }
        observe(tagReveal(c, 'fade-up', 0));
      });
    });

    // Replo alchemy fade elements: resume (bg heroes) or override with CSS reveal.
    var nodes = document.querySelectorAll('[data-rid]');
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.__airRevealTagged || node.__airRevealed) continue;
      if (node.closest('[data-replo-carousel]')) continue;
      if (!isAlchemyPaused(node)) continue;
      if (hasBg(node)) { alchemyBg.push(node); continue; }
      node.classList.add('air-reveal--alchemy-override');
      observe(tagReveal(node, 'fade-up', 0));
    }

    function playAlchemy(el) { el.style.animationPlayState = 'running'; }
    if (alchemyBg.length) {
      if (!('IntersectionObserver' in window) || reducedMotion) {
        alchemyBg.forEach(playAlchemy);
      } else {
        var aio = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) { playAlchemy(e.target); aio.unobserve(e.target); }
          });
        }, { rootMargin: '0px 0px -5% 0px', threshold: 0.01 });
        alchemyBg.forEach(function (el) { if (isInView(el)) playAlchemy(el); else aio.observe(el); });
      }
    }

    if (!toObserve.length) return;
    if (reducedMotion || !('IntersectionObserver' in window)) {
      toObserve.forEach(revealEl);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { revealEl(e.target); io.unobserve(e.target); }
      });
    }, REVEAL_OPTS);
    toObserve.forEach(function (el) { if (isInView(el)) revealEl(el); else io.observe(el); });
  }

  /* ---------------- Product card nav ---------------- */
  function initProductNav() {
    each(document.querySelectorAll('[data-replo-product-handle]'), function (card) {
      if (card.__airNav) return;
      var handle = (card.getAttribute('data-replo-product-handle') || '').trim();
      if (!handle || handle === 'empty-product') return;
      // Skip the on-page buy box: a product container with its own cart form /
      // collapsibles is the current product, not a click-through tile.
      if (
        card.querySelector('form[action*="/cart"], [data-replo-add-product-variant-to-cart], [data-replo-collapsible]')
      ) {
        return;
      }
      card.__airNav = true;
      card.addEventListener('click', function (e) {
        if (e.target.closest('a[href], button, form, input, select, [role="button"]')) return;
        window.location.href = '/products/' + handle;
      });
    });
  }

  ready(function () {
    each(document.querySelectorAll('[data-replo-carousel]'), initCarousel);
    each(document.querySelectorAll('[data-replo-collapsible]'), initCollapsible);
    initProductNav();
    initMotion();
  });
})();
